<template>
  <div class="overview-content">
    <!-- Plan Cards -->
    <div class="card-container">
      <PlanCard v-if="leftPlan" :plan="leftPlan" :isCurrent="true" />
      <div v-else class="blank"></div>
      <PlanCard v-if="middlePlan" :plan="middlePlan" :isCurrent="false" @change-tab="openPlans()" />
      <div v-else class="blank"></div>
      <PlanCard v-if="rightPlan" :plan="rightPlan" :isCurrent="false" />
      <div v-else class="blank"></div>
    </div>

    <!-- Account Summary -->
    <div class="account-summary">
      <div class="d-flex justify-content-between">
        <div>
          <h5 style="font-size: 19px;">Account Summary</h5>
          <span style="opacity: 50%; margin-top: 4px;">Total charges and payment information.</span>
        </div>
        <vue3-select class="vue-select" v-model="selectedMonth" :options="months" label="label"
          placeholder="Select an option" />
      </div>
      <div class="mt-5 metadata">
        <div class="col-3">
          <span class="info">Billing Period</span><br>
          <span style="font-weight: 600;">{{ monthStart }} - {{ monthEnd }}</span>
        </div>
      </div>

      <!-- Invoice Table -->
      <div class="mt-4 invoice-container" v-if="invoices.length > 0">
        <table class="invoice-table">
          <tbody>
            <template v-for="(invoice, index) in invoices" :key="invoice.id">
              <!-- Main Invoice Row -->
              <tr class="invoice-row" :class="{ expanded: invoice.expanded }">
                <td class="invoice-cell">
                  <div style="display: flex; align-items: center;">
                    <span class="expand-icon" :class="{ expanded: invoice.expanded }" @click="toggleInvoice(index)">
                      <svg viewBox="0 0 24 24">
                        <path d="M9 5l7 7-7 7V5z" />
                      </svg>
                    </span>
                    <div>
                      <div class="invoice-name" @click="toggleInvoice(index)">
                        Invoice : {{ invoice.name }}
                      </div>
                      <div class="invoice-date">{{ invoice.date }} | Total items: {{ invoice.items.length }}</div>
                    </div>
                  </div>
                </td>
                <td class="invoice-cell amount">
                  <span class="currency">{{ invoice.currency }}</span>{{ invoice.total.toFixed(2) }}
                </td>
              </tr>

              <!-- Expandable Details Row -->
              <tr v-if="invoice.expanded" class="details-row">
                <td colspan="2" class="invoice-cell">
                  <transition name="slide">
                    <div class="details-container">
                      <table class="details-table">
                        <tr class="details-header">
                          <td>Item</td>
                          <td style="text-align: right;">Amount</td>
                        </tr>
                        <tr v-for="item in invoice.items" :key="item.id" class="details-item">
                          <td class="item-name">{{ item.name }}</td>
                          <td class="item-amount">
                            <span class="currency">{{ invoice.currency }}</span>{{ item.amount.toFixed(2) }}
                          </td>
                        </tr>
                        <tr class="total-row">
                          <td style="padding-top: 12px;">
                            <strong>Total</strong>
                          </td>
                          <td class="item-amount" style="padding-top: 12px;">
                            <strong>
                              <span class="currency">{{ invoice.currency }}</span>{{ invoice.total.toFixed(2) }}
                            </strong>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </transition>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-else-if="isFreeTrial" class="mt-4 no-data">
        No invoices are generated for Free trial plan.
      </div>
      <!-- Show message when no invoices -->
      <div v-else class="mt-4 no-data">
        No invoices available for the selected period.
      </div>

      <!-- Buttons -->
      <div class="btn-container">
        <button class="invoice-btn" @click="viewAllInvoices()" v-if="!isFreeTrial">View All Invoices</button>
        <Tooltip :tooltipText="`Button wil be disabled for current month`" position="top">
          <button class="dn-btn" @click="generateExcelForBasic()" v-if="!isFreeTrial" :disabled="isCurrentMonth"
            :class="{ 'disabled-btn': isCurrentMonth }">Download Usage </button>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import PlanCard from './MiniComponents/PlanCard.vue';
import moment from 'moment';
import "vue3-select/dist/vue3-select.css";
import Vue3Select from "vue3-select";
import { mapState, mapActions } from "vuex";
import * as XLSX from 'xlsx';
import Tooltip from "../modules/tooltip.vue";

export default {
  name: 'OverviewContent',
  components: {
    PlanCard,
    Vue3Select,
    Tooltip,
  },
  data() {
    return {
      currentPlan: '', // 'grow', 'scale' , 'enterprise'
      plans: {
        'Free Trial': {
          name: 'Free Trial',
          description: 'Trial period for new users',
          price: '0',
          buttons: [{ text: 'Upgrade Plan', class: 'free-upgrade-btn' }]
        },
        Startup: {
          name: 'Startup Plan',
          description: 'Upto 2 team members',
          price: '6000',
          buttons: [
            { text: 'Upgrade Plan', class: 'upgrade-btn' }
          ]
        },
        Grow: {
          name: 'Grow Plan',
          description: 'Upto 5 team members',
          price: '25000',
          buttons: [{ text: 'Upgrade Plan', class: 'upgrade-btn' }]
        },
        Scale: {
          name: 'Scale Plan',
          description: 'Upto 10 team members',
          price: '40000',
          buttons: [{ text: 'Upgrade Plan', class: 'upgrade-btn' }]
        },
        enterprise: {
          name: 'Enterprise Plan',
          description: 'No limitation on team members',
          price: 'Custom',
          buttons: [{ text: 'Talk to an Expert', class: 'upgrade-btn' }]
        }
      },
      months: [],
      selectedMonth: '',
      fromDate: moment().subtract(1, 'month').startOf('month').format('DD/MM/YYYY'),
      toDate: moment().startOf('month').format('DD/MM/YYYY'),
      monthEnd: '',
      monthStart: '',
      excelData: [],
      invoiceDetails: [],
      invoices: [],
      isFreeTrial: false,
      isCurrentMonth: false
    };
  },
  methods: {
    ...mapActions([
      "GetBillingDetails", 'postBasicConsolidatedDetails', 'GetMonthlyInvoiceDetails'
    ]),
    toggleInvoice(index) {
      this.invoices[index].expanded = !this.invoices[index].expanded;
    },
    viewAllInvoices() {
      this.$emit('change-tab', 'paymenthistory');
    },
    async generateExcelForBasic() {
      const start = this.fromDate;
      let endDate = this.toDate;
      let sendClientId = this.user.clientId
      var value = await this.postBasicConsolidatedDetails({
        clientId: sendClientId,
        fromDate: start,
        toDate: endDate
      });
      console.log(value);

      var newData = value.object?.data;
      newData.forEach((data) => {
        this.excelData.push({
          ClientId: data.clientId,
          ClientName: data.clientName || "NA",
          TestId: data.testId || "NA",
          TestName: data.testName || "NA",
          CandidateID: data.candidateID,
          CandidateName: data.candidateName || "NA",
          EmailAddress: data.emailAddress || "NA",
          Mobile: data.mobile || "NA",
          TestStartTime: data.testStartTime || "NA",
          TestEndTime: data.testEndTime || "NA",
          Region: data.region || "NA",
          OverallFitment: data.overallFitment || "NA",
          AptitudeFitment: data.aptitudeFitment || "NA",
          PsychometryFitment: data.psychometryFitment || "NA",
        });
      });
      if (this.excelData.length != 0) {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(this.excelData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, 'BasicConsolidateSheet.xlsx');
        this.resetModal();
      }
    },
    resetModal() {
      this.excelData = [];
    },
    openPlans() {
      this.$emit('change-tab', 'plans');
    },

    transformInvoiceData(apiData) {
      const groupedData = {};

      apiData.forEach((item, index) => {
        const key = item.invoiceNumber || `Invoice-${index + 1}`;

        if (!groupedData[key]) {
          groupedData[key] = {
            id: index + 1,
            name: key,
            invoiceNumber: item.invoiceNumber,
            date: moment(item.createdDate).format('MMMM DD, YYYY'),
            currency: this.user.countryCode === 'IN' ? '₹' : '$',
            expanded: false,
            items: [],
            total: 0
          };
        }

        groupedData[key].items.push({
          id: item.id || index,
          name: item.testName,
          amount: parseFloat(item.amount) || 0
        });

        groupedData[key].total += parseFloat(item.amount) || 0;
      });

      return Object.values(groupedData);
    },
    async GetMonthlyInvoice() {
      const res = await this.GetMonthlyInvoiceDetails({
        clientId: this.user.clientId, //this.user.clientId 1361
        startDate: this.fromDate, //this.fromDate 2024-01-01
        endDate: this.toDate //this.toDate 2025-01-01
      });
      return res;
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    grandTotal() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.total, 0);
    },
    totalAmount() {
      let total = 0;
      this.invoiceDetails.forEach(row => {
        total += row.amount
      });
      return '₹' + total.toFixed(2);
    },
    leftPlan() {
      return this.plans[this.currentPlan];
    },
    middlePlan() {
      switch (this.currentPlan) {
        case 'Free Trial':
          return this.plans.Startup;
        case 'Startup':
          return this.plans.Grow;
        case 'Grow':
          return this.plans.Scale;
        case 'Scale':
          return this.plans.enterprise;
        case 'enterprise':
          return null;
        default:
          return null;
      }
    },
    rightPlan() {
      if (this.currentPlan === 'Free Trial' || this.currentPlan === 'Startup' || this.currentPlan === 'Grow') {
        return this.plans.enterprise;
      }
      return null;
    },
  },
  created() {
    if (this.user.countryCode) {
      if (this.user.countryCode !== 'IN') {
        this.plans.Startup.price = '100';
        this.plans.Grow.price = '400';
        this.plans.Scale.price = '1500';
      }
    }
  },
  async mounted() {
    const startMonth = moment().subtract(1, 'year').startOf('year');
    const endMonth = moment().endOf('month'); //.subtract(1, 'month')
    let current = startMonth.clone();
    while (current.isSameOrBefore(endMonth, 'month')) {
      this.months.push(current.format('MMMM YYYY'));
      current.add(1, 'month');
    }
    this.selectedMonth = this.months[this.months.length - 1];

    if (this.user.isEnterprise == false) {
      var data = await this.GetBillingDetails({
        clientId: this.user.clientId
      })
      this.currentPlan = data.object.data.plan.planName;
      this.isFreeTrial = this.currentPlan === 'Free Trial' ? true : false;
    } else {
      this.currentPlan = 'enterprise';
    }
  },
  watch: {
    selectedMonth(newVal) {
      if (newVal) {
        this.fromDate = moment(newVal, 'MMMM YYYY').startOf('month').format('YYYY-MM-DD');
        this.toDate = moment(newVal, 'MMMM YYYY').add(1, 'month').startOf('month').format('YYYY-MM-DD');
        this.monthEnd = moment(this.toDate, 'YYYY-MM-DD').subtract(1, 'days').format('DD/MM/YYYY');
        this.monthStart = moment(this.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
        const currentMonthStart = moment().startOf('month').format('YYYY-MM-DD');
        this.isCurrentMonth = (this.fromDate === currentMonthStart);

        this.GetMonthlyInvoice().then((res) => {
          if (res.status) {
            this.invoiceDetails = res.object.data;
            this.invoices = this.invoiceDetails.length > 0 ? this.transformInvoiceData(this.invoiceDetails) : [];
          } else {
            this.invoices = [];
          }
        }).catch((error) => {
          console.error("Error fetching invoices:", error);
          this.invoices = [];
        });

      } else {
        this.fromDate = '';
        this.toDate = '';
        this.monthEnd = '';
        this.monthStart = '';
        this.invoices = [];
      }
    },
    immediate: true
  },
};
</script>

<style scoped>
.table-container {
  max-height: 25rem;
  overflow-y: auto;
  border: 1px solid gray;
  border-radius: 10px;
}

.btn-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  height: 3rem;
}

.dn-btn {
  background: #4F46E5;
  color: white;
  width: 11rem;
  border-radius: 7px;
  border: none;
  padding: 10px 0px;
  margin-left: 8px;
}

td {
  padding: 15px;
  text-align: center;
}

.vue-select {
  width: 20%;
}

.metadata {
  display: flex;
}

.overview-content {
  padding: 20px;
}

.card-container {
  display: flex;
  justify-content: space-around;
  margin-left: -1.3rem;
  flex-wrap: wrap;
}

.blank {
  width: 200px;
  height: 200px;
  margin-right: 9rem;
}

/* account summary */
.account-summary {
  background: white;
  display: flex;
  flex-direction: column;
  border: none;
  box-shadow: 0 1px 3px rgba(101, 101, 101, 0.3);
  border-radius: 8px;
  margin-top: 2rem;
  padding: 20px 16px;
  width: 96.2%;
  margin-left: 0.9rem;
}

.invoice-btn {
  background: transparent;
  color: blue;
  width: 10rem;
  border-radius: 7px;
  border-color: blue;
}

.info {
  opacity: 50%;
}

.teenyiconstick-circle-outline {
  width: 15px;
  position: relative;
  max-height: 100%;
  overflow: hidden;
  flex-shrink: 0;
}

.issued {
  position: relative;
  font-weight: 500;
}

.teenyiconstick-circle-outline-parent {
  position: relative;
  border-radius: 8px;
  background-color: rgba(0, 125, 71, 0.1);
  width: 25%;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  box-sizing: border-box;
  gap: 10px;
  text-align: left;
  font-size: 16px;
  color: #007d47;
  font-family: 'DM Sans';
}

/* Invoice Table Styles */
.invoice-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 20px;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.invoice-row {
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.invoice-row:hover {
  background-color: #f8f9fa;
}

.invoice-row.expanded {
  background-color: #f0f8ff;
}

.invoice-cell {
  padding: 12px 16px;
  text-align: left;
  vertical-align: middle;
}

.invoice-cell.amount {
  text-align: right;
  font-weight: 500;
}

.expand-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.expand-icon svg {
  width: 12px;
  height: 12px;
  fill: #666;
}

.invoice-name {
  font-weight: 600;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.invoice-name:hover {
  color: #0066cc;
}

.invoice-date {
  color: #666;
  font-size: 12px;
}

.details-row {
  background-color: #f8f9fa;
  border-left: 4px solid #0066cc;
}

.details-container {
  padding: 0 16px 16px 40px;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}

.details-header {
  font-weight: 600;
  color: #444;
  border-bottom: 2px solid #e0e0e0;
  padding: 8px 0;
}

.details-item {
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.details-item:last-child {
  border-bottom: none;
}

.item-name {
  color: #333;
}

.item-amount {
  text-align: right;
  font-weight: 500;
  color: #333;
}

.total-row {
  border-top: 2px solid #e0e0e0;
  font-weight: 600;
  color: #333;
  background-color: #fff;
}

.slide-enter-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.currency {
  color: #666;
  font-size: 12px;
  margin-right: 4px;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

.disabled-btn {
  opacity: 0.5;
  pointer-events: none;
  /* Prevent clicks just in case */
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .vue-select {
    width: 67%;
  }

  .metadata {
    display: block;
  }

  .metadata .col-3,
  .metadata .col-4 {
    width: 100%;
    margin-bottom: 10px;
  }

  .teenyiconstick-circle-outline-parent {
    width: 30%;
  }

  .blank {
    display: none;
  }

  .account-summary {
    width: 105%;
    margin-left: 0px !important;
  }
}
</style>